/**
 * The chat widget: state and render.
 *
 * Two components. `ChatWidget` resolves WHERE the configuration comes from —
 * the server (GET /chat/config), the props, or nothing at all when the server
 * says this page must not show an assistant — and renders nothing until that
 * is settled. `ChatPanel` is the widget proper, mounted only once, with its
 * greeting and chips initialised from the RESOLVED configuration; mounting it
 * earlier would seed the transcript with the snippet's greeting and paint the
 * server's over it a moment later.
 *
 * The pieces this file used to hold live beside it: config.ts (resolution),
 * storage.ts (IndexedDB), useHistoryPolling.ts (operator replies), theme.ts
 * (colour + styles), icons.tsx, DetailsForm.tsx + details.ts (the one details
 * form, ADR-110 Am.6), lead.ts (lead capture), actions.ts (booking steps,
 * ADR-110), FeedbackForm.tsx
 * + feedback.ts ("Send feedback", for the Ema Answers team), humanCheck.ts +
 * HumanCheckFrame.tsx (the human check, ADR-123), markdown.ts (the one
 * HTML-producing module, with tests) and strings.ts.
 */
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { renderMarkdown, stripMarkdown } from './markdown';
import { fill } from './strings';
import { fetchServerConfig, resolveConfig, type BlockedCode, type ResolvedConfig } from './config';
import { loadChat, saveChat, clearChat, isFresh } from './storage';
import { useHistoryPolling } from './useHistoryPolling';
import { onCommand } from './commands';
import { buildStyles, sendButtonStyle, srOnlyStyle, KEYFRAMES_STYLE_ID, KEYFRAMES_CSS } from './theme';
import { ChatIcon, CloseIcon, FeedbackIcon, PlusIcon, CheckIcon, ExpandIcon, CollapseIcon, SendIcon, RetryIcon } from './icons';
import { FeedbackForm } from './FeedbackForm';
import { feedbackPage, feedbackRequest, submitFeedback, type FeedbackInput } from './feedback';
import { DetailsForm } from './DetailsForm';
import { submitLead, summariseConversation, type LeadInput, type LeadOutcome } from './lead';
import { streamMessage } from './stream';
import { isHumanCheckRefusal } from './humanCheck';
import { HumanCheckError, useHumanCheck } from './HumanCheckFrame';
import {
  bookingChoices,
  bookingDetailsLine,
  formatSlotLabel,
  isBookingControl,
  parseActions,
  type BookingChoiceKind,
  type BookingDetails,
  type ChatAction,
} from './actions';
import type { ChatWidgetProps, Message } from './types';

export type { ChatWidgetProps } from './types';

/**
 * A /chat/message call that has not answered by then is abandoned and shown
 * as a connection failure with a retry. The backend's own Lambda timeout is
 * 30 s; without this the typing dots ran for as long as the browser allowed.
 */
const MESSAGE_TIMEOUT_MS = 35_000;

/**
 * Canned backend replies arrive with a `code` beside the English `reply`; the
 * visitor gets the widget's own copy in their language instead (review S10).
 * A reply without a code is a real answer and is shown as sent.
 */
const CANNED_REPLY_KEYS = {
  no_content: 'noContent',
  over_cap: 'overCap',
  unavailable: 'unavailable',
  refused: 'refused',
} as const;

type PanelSize = 'default' | 'expanded';

type Chip = { kind: 'text'; text: string } | { kind: 'lead' } | { kind: 'link'; label: string; url: string };

/**
 * One turn as it goes on the wire. `message` is what the visitor typed or
 * tapped — for a booking step, the value the server reads (a slot's instant,
 * `yes`) rather than the label in their bubble — and a details form adds
 * `details` (ADR-110). A retry resends exactly this.
 */
interface OutgoingTurn {
  message: string;
  details?: BookingDetails;
}

/** The test hook on each booking chip. */
const CHOICE_PWE: Record<BookingChoiceKind, string> = {
  option: 'booking-option',
  slot: 'booking-slot',
  yes: 'booking-confirm-yes',
  no: 'booking-confirm-no',
};

/** Whether a link chip points at the page's own site, which then opens in place. */
function isSameOrigin(url: string): boolean {
  try {
    return typeof location !== 'undefined' && new URL(url).origin === location.origin;
  } catch {
    return false;
  }
}

function explainBlocked(code: BlockedCode, tenantId: string, apiUrl: string): void {
  const origin = typeof location !== 'undefined' ? location.origin : 'this site';
  switch (code) {
    case 'tenant_unknown':
      console.error(
        `[PweChat] Tenant ${JSON.stringify(tenantId)} is unknown to ${apiUrl} — the widget was not shown. ` +
          'Check the tenantId in your snippet against Setup → Install in the Ema Answers console.',
      );
      return;
    case 'origin_not_allowed':
      console.error(
        `[PweChat] ${origin} is not on the list of sites this assistant may run on — the widget was not shown. ` +
          'The list follows the website saved under Knowledge › From your website in the Ema Answers console (that site and its subdomains).',
      );
      return;
    case 'assistant_unavailable':
      console.info('[PweChat] This assistant is not available right now; the widget was not shown.');
      return;
  }
}

export function ChatWidget(props: ChatWidgetProps) {
  const { apiUrl, tenantId, fetchConfig = true } = props;

  // The props-only resolution, kept live for surfaces that drive the
  // configuration themselves (the console's Personalise preview re-renders
  // with every keystroke in the colour field). Objects in the deps are
  // reduced to primitives so a fresh `offset={{...}}` literal does not force
  // a resolve — and a console.warn for a bad colour — on every render.
  const fromProps = useMemo(
    () => (fetchConfig ? null : resolveConfig(props, null)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchConfig,
      props.language,
      props.businessName,
      props.greeting,
      props.brandColor,
      props.avatarUrl,
      props.privacyUrl,
      props.feedbackApiUrl,
      props.launcherSelector,
      props.linkChipLabel,
      props.linkChipUrl,
      props.position,
      props.leadCaptureEnabled,
      JSON.stringify(props.leadForm ?? null),
      props.offset?.bottom,
      props.offset?.side,
      JSON.stringify(props.defaultSuggestions ?? []),
    ],
  );

  const [fromServer, setFromServer] = useState<ResolvedConfig | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!fetchConfig) return;
    const ctrl = new AbortController();
    let active = true;
    fetchServerConfig(apiUrl, tenantId, { signal: ctrl.signal }).then((outcome) => {
      if (!active) return;
      if (outcome.kind === 'ok') {
        if (!outcome.config.chatbotEnabled) {
          console.info('[PweChat] The assistant is switched off in the Ema Answers console; the widget was not shown.');
          setHidden(true);
          return;
        }
        setFromServer(resolveConfig(props, outcome.config));
        return;
      }
      if (outcome.kind === 'fallback') {
        console.warn(
          `[PweChat] Could not load the assistant configuration from ${apiUrl} (${outcome.reason}); using the snippet options.`,
        );
        setFromServer(resolveConfig(props, null));
        return;
      }
      explainBlocked(outcome.code, tenantId, apiUrl);
      setHidden(true);
    });
    return () => {
      active = false;
      ctrl.abort();
    };
    // The snippet's props do not change on a real site; the console preview
    // passes fetchConfig={false} and takes the memo above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, tenantId, fetchConfig]);

  if (hidden) return null;
  const config = fetchConfig ? fromServer : fromProps;
  if (!config) return null;
  return <ChatPanel props={props} config={config} />;
}

function ChatPanel({ props, config }: { props: ChatWidgetProps; config: ResolvedConfig }) {
  const {
    apiUrl,
    tenantId,
    externalTrigger = false,
    toggleEvent = 'assistbar:toggle-chat',
    enableCart = false,
    defaultOpen = false,
    persist = true,
  } = props;
  const {
    strings: ui,
    language,
    businessName,
    greeting,
    brandColor,
    avatarUrl,
    position,
    offset,
    privacyUrl,
    feedbackApiUrl,
    launcherSelector,
    linkChip,
    leadCaptureEnabled,
    leadForm,
    launcherLabel,
    streamUrl,
  } = config;
  // Copy a snippet may still override; the localised default otherwise.
  const errorMessage = props.errorMessage || ui.errorMessage;
  const originBlockedMessage = props.originBlockedMessage || ui.originBlockedMessage;
  const subtitle = props.subtitle || ui.subtitle;
  const privacyNotice = props.privacyNotice || ui.privacyNotice;
  const privacyLinkLabel = props.privacyLinkLabel || ui.privacyLinkLabel;
  // The page opens the chat from its own control — a `window` event
  // (`externalTrigger`) or its own button (`launcherSelector`) — so the widget
  // draws no launcher of its own.
  const hostLaunched = externalTrigger || Boolean(launcherSelector);

  const nextIdRef = useRef(1);
  const newId = () => `m${nextIdRef.current++}`;

  const [open, setOpen] = useState(defaultOpen);
  const [panelSize, setPanelSize] = useState<PanelSize>('default');
  const [messages, setMessages] = useState<Message[]>([{ id: 'greeting', role: 'assistant', content: greeting }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>(config.suggestions);
  const [restored, setRestored] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [lastFailed, setLastFailed] = useState<OutgoingTurn | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  // Message id currently being revealed with the typewriter effect, plus how
  // many characters of it are visible.
  const [reveal, setReveal] = useState<{ id: string; chars: number } | null>(null);
  // Polling stops for this conversation: 403/404 (not ours any more) or the
  // operator closed/archived it. A new chat starts it again.
  const [pollStopped, setPollStopped] = useState(false);
  // Epoch ms of the visitor's last send / open / return to the tab: the
  // polling schedule goes hot again from here.
  const [activityAt, setActivityAt] = useState(() => Date.now());
  // Lead capture.
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadDisabled, setLeadDisabled] = useState(false);
  const [leadSuggested, setLeadSuggested] = useState(false);
  const [leadChipHidden, setLeadChipHidden] = useState(false);
  // The link chip steps aside with the suggestions (the ×) until the next reply.
  const [linkChipHidden, setLinkChipHidden] = useState(false);
  // Booking steps (ADR-110): the latest assistant turn's actions, drawn below
  // it and cleared by the next send. Never persisted — after a reload they are
  // gone and typing carries on, because the server holds the booking's state.
  const [actions, setActions] = useState<ChatAction[]>([]);
  // "Send feedback" (feedback.ts): the form stands in for the conversation
  // while it is open. Never persisted; closing the panel closes it too.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // The human check (ADR-123): a pass for this tenant, and the frame the
  // challenge runs in. Configured by the server; learnt from a refusal when
  // this surface mounted without the server's configuration.
  const { check: humanCheck, frame: humanCheckFrame } = useHumanCheck({
    apiUrl,
    tenantId,
    config: config.humanCheck,
    language,
    persist,
    title: ui.humanCheckTitle,
    visible: open,
  });

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const sessionIdRef = useRef<string | null>(null);
  sessionIdRef.current = sessionId;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const feedbackButtonRef = useRef<HTMLButtonElement>(null);
  const wasFeedbackOpenRef = useRef(false);
  // The page's own button that last opened the panel: focus goes back to it.
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seenAdminIdsRef = useRef<Set<string>>(new Set());
  const lastSkRef = useRef<string | null>(null);

  const reducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const bumpActivity = useCallback(() => setActivityAt(Date.now()), []);

  // ── Persistence ──────────────────────────────────────────────
  useEffect(() => {
    if (!persist) {
      // Ephemeral (preview) mode: always start from the CURRENT config.
      setRestored(true);
      return;
    }
    let active = true;
    loadChat(tenantId).then((record) => {
      if (!active) return;
      // loadChat has already checked the shape and the tenant; a stale record
      // is left alone and overwritten by the first save.
      if (record && isFresh(record) && record.messages.length > 0) {
        // The first bubble is always the greeting (initial state, and "new
        // chat" resets to it). Show the greeting the operator has NOW, not
        // the one saved up to 24 h ago — the README promises a console change
        // reaches every site on its next page load, returning visitors too.
        const [first, ...rest] = record.messages;
        const current = first.role === 'assistant' && !first.error ? { ...first, content: greeting } : first;
        setMessages([current, ...rest].map((m) => ({ ...m, id: newId() })));
        setSuggestions(record.suggestions.length > 0 ? record.suggestions : config.suggestions);
        if (record.sessionId) setSessionId(record.sessionId);
        seenAdminIdsRef.current = new Set(record.adminIds ?? []);
        lastSkRef.current = record.lastSk ?? null;
        setLeadSubmitted(record.leadSubmitted === true);
      }
      setRestored(true);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!restored || !persist) return;
    saveChat(tenantId, {
      tenantId,
      messages: messages.map(({ role, content, error }) => ({ role, content, ...(error ? { error } : {}) })),
      suggestions,
      updatedAt: Date.now(),
      ...(sessionId ? { sessionId } : {}),
      adminIds: Array.from(seenAdminIdsRef.current),
      ...(lastSkRef.current ? { lastSk: lastSkRef.current } : {}),
      ...(leadSubmitted ? { leadSubmitted } : {}),
    });
  }, [messages, suggestions, sessionId, restored, leadSubmitted, persist, tenantId]);

  // ── Scrolling, focus, open/close ─────────────────────────────
  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // The latest turn's booking steps are drawn once it has settled: not while
  // a send is out, while the reply is still being typed out, or after a failure.
  const bookingSettled = actions.length > 0 && !loading && !reveal && !lastFailed;

  useEffect(() => {
    scrollToBottom();
  }, [messages, leadOpen, bookingSettled, scrollToBottom]);

  // The check starts when the panel opens rather than when the visitor sends:
  // by the first message it has usually finished, so `enforce` adds no wait,
  // and `log` also measures the visitors who open the chat and never write.
  useEffect(() => {
    if (!open || !humanCheck.mode() || humanCheck.current()) return;
    humanCheck.obtain().catch(() => undefined);
  }, [open, humanCheck]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    // Scroll to bottom when chat opens (e.g. after page refresh with restored history)
    const t = setTimeout(() => scrollToBottom(), 100);
    bumpActivity();
    return () => clearTimeout(t);
  }, [open, scrollToBottom, bumpActivity]);

  // Back from the feedback form. The transcript was hidden, which drops its
  // scroll position, so land at its end — with the caret in the composer on a
  // desktop, and on the header button on a phone, where focusing the input
  // would throw the keyboard up over the conversation.
  useEffect(() => {
    if (wasFeedbackOpenRef.current && !feedbackOpen && open) {
      scrollToBottom(false);
      if (isMobile) feedbackButtonRef.current?.focus();
      else inputRef.current?.focus();
    }
    wasFeedbackOpenRef.current = feedbackOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackOpen]);

  /**
   * `fromInside` = the close came from the panel (its close button, Escape
   * with focus in it), so focus goes back to the launcher. A close from the
   * host page's own trigger leaves the host's focus where it is (review S13).
   */
  const closePanel = useCallback(
    (fromInside: boolean) => {
      setOpen(false);
      setPanelSize('default');
      setFeedbackOpen(false);
      if (!fromInside) return;
      if (!hostLaunched) {
        setTimeout(() => launcherRef.current?.focus(), 0);
      } else if (lastTriggerRef.current?.isConnected) {
        // Back to the page's own button, when one opened it.
        const trigger = lastTriggerRef.current;
        setTimeout(() => trigger.focus(), 0);
      }
    },
    [hostLaunched],
  );

  // Escape is handled ON the panel, not on window: a host lightbox's Escape
  // used to close the chat too and yank focus to the corner.
  const onPanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closePanel(true);
    }
  };

  // A host page's own control, by event (`externalTrigger`): puglieseweb.com's
  // AssistBar dispatches `assistbar:toggle-chat`.
  useEffect(() => {
    if (!externalTrigger) return;
    const handler = () => setOpen((prev) => !prev);
    window.addEventListener(toggleEvent, handler);
    return () => window.removeEventListener(toggleEvent, handler);
  }, [externalTrigger, toggleEvent]);

  // PweChat.open() / close() / toggle() (commands.ts), whatever else opens the panel.
  useEffect(
    () => onCommand((command) => setOpen((prev) => (command === 'toggle' ? !prev : command === 'open'))),
    [],
  );

  // A host page's own button, by selector (`launcherSelector`). One listener on
  // the document, in the capture phase, rather than one on each element: a
  // site builder renders its buttons late, re-renders them and adds more, and
  // a host handler that stops propagation must not swallow the click. The
  // widget's own panel is never a trigger, whatever the selector matches —
  // "button" would otherwise shut it on every click inside it.
  useEffect(() => {
    if (!launcherSelector) return;
    try {
      if (!document.querySelector(launcherSelector)) {
        console.warn(
          `[PweChat] Nothing on this page matches launcherSelector ${JSON.stringify(launcherSelector)} yet; the chat opens when an element that matches it is clicked.`,
        );
      }
    } catch {
      console.error(
        `[PweChat] launcherSelector ${JSON.stringify(launcherSelector)} is not a valid CSS selector, so nothing on this page opens the chat. Fix it in the Ema Answers console (Setup → Install).`,
      );
      return;
    }
    const onClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('[data-pwe="panel"]')) return;
      const trigger = target.closest(launcherSelector);
      if (!(trigger instanceof HTMLElement)) return;
      // The client named this element as their chat button: it opens the chat
      // and does nothing else, even when it is a link.
      event.preventDefault();
      lastTriggerRef.current = trigger;
      setOpen((prev) => !prev);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [launcherSelector]);

  // The page's own buttons say whether the chat they control is open.
  useEffect(() => {
    if (!launcherSelector) return;
    try {
      document.querySelectorAll(launcherSelector).forEach((el) => el.setAttribute('aria-expanded', String(open)));
    } catch {
      // An unparseable selector was reported once, above.
    }
  }, [open, launcherSelector]);

  // Coming back to the tab restarts polling hot; nothing polls while hidden.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') bumpActivity();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [bumpActivity]);

  // ── Typewriter reveal ────────────────────────────────────────
  useEffect(() => {
    if (!reveal) return;
    const msg = messages.find((m) => m.id === reveal.id);
    if (!msg || reveal.chars >= msg.content.length) {
      setReveal(null);
      return;
    }
    // ~24ms per tick, sized so even long replies finish in ~2.5s.
    const step = Math.max(2, Math.ceil(msg.content.length / 100));
    const t = setTimeout(() => {
      setReveal((r) => (r && r.id === reveal.id ? { ...r, chars: r.chars + step } : r));
      scrollToBottom(false);
    }, 24);
    return () => clearTimeout(t);
  }, [reveal, messages, scrollToBottom]);

  const startReveal = (id: string) => {
    if (!reducedMotion) setReveal({ id, chars: 0 });
  };

  const pushAssistant = (content: string, error = false) => {
    const id = newId();
    setMessages((prev) => [...prev, { id, role: 'assistant', content, ...(error ? { error: true } : {}) }]);
    setAnnouncement(stripMarkdown(content));
    return id;
  };

  /**
   * The server may hand back a different sessionId at any turn (an expired
   * or re-parented conversation gets a fresh one). Adopt it every time; the
   * poll cursor and the seen operator ids belonged to the old conversation.
   */
  const adoptSession = (id: unknown) => {
    if (typeof id !== 'string' || !id) return;
    if (id !== sessionIdRef.current) {
      seenAdminIdsRef.current = new Set();
      lastSkRef.current = null;
      setPollStopped(false);
      setSessionId(id);
    }
  };

  // ── Send / retry ─────────────────────────────────────────────

  /** The assistant bubble a stream is writing into, while one is. */
  const streamIdRef = useRef<string | null>(null);
  const [streaming, setStreaming] = useState(false);

  /**
   * A finished turn — the REST body, or the stream's `done` (ADR-103 §2).
   * Both carry the same fields. When a stream wrote a bubble, its text is
   * REPLACED with `reply`: the server checks the whole reply after the
   * stream (a link it drops, a comment it strips, a leak it refuses), and
   * what it settled on is the reply, not what was previewed.
   */
  const applyReply = (data: Record<string, unknown>) => {
    adoptSession(data.sessionId);

    const code = typeof data.code === 'string' ? (data.code as keyof typeof CANNED_REPLY_KEYS) : null;
    const cannedKey = code ? CANNED_REPLY_KEYS[code] : undefined;
    const replyText = cannedKey ? ui[cannedKey] : typeof data.reply === 'string' ? data.reply : '';
    if (!replyText) throw new Error('empty reply');

    const streamed = streamIdRef.current;
    streamIdRef.current = null;
    if (streamed) {
      setMessages((prev) => prev.map((m) => (m.id === streamed ? { ...m, content: replyText } : m)));
      setAnnouncement(stripMarkdown(replyText));
    } else {
      const replyId = pushAssistant(replyText);
      startReveal(replyId);
    }
    setLastFailed(null);
    setLeadChipHidden(false);
    setLinkChipHidden(false);

    setSuggestions(Array.isArray(data.suggestions) ? data.suggestions.filter((s: unknown) => typeof s === 'string') : []);

    // The booking steps belong to THIS turn (ADR-110): a reply without any
    // clears the last turn's. Unknown or malformed actions are dropped, and
    // the reply text above stands on its own.
    const turnActions = parseActions(data.actions);
    setActions(turnActions);

    // `capture_lead` asks for the lead form the way the model's own
    // suggestion does: the chip comes first, when lead capture is on.
    const wantsLead = data.suggest_lead_capture === true || turnActions.some((a) => a.type === 'capture_lead');
    if (wantsLead && leadCaptureEnabled) setLeadSuggested(true);

    // Cart sync (LEGACY, opt-in)
    if (enableCart && data.cartId) {
      window.dispatchEvent(new CustomEvent('basket:sync', { detail: { cartId: data.cartId } }));
    }
  };

  /**
   * 403 with one of our codes: this page is not on the tenant's allowed
   * site list, or the assistant is off. Retrying cannot fix either, so say
   * so plainly and stop offering a retry — otherwise a misconfigured
   * install just looks like an outage, and the person who can fix it never
   * learns why. Returns true when it was one of those.
   */
  const applyBlocked = (status: number, detail: Record<string, unknown> | null): boolean => {
    if (status !== 403 || !detail) return false;
    if (detail.code === 'origin_not_allowed') {
      setLastFailed(null);
      pushAssistant(originBlockedMessage, true);
      return true;
    }
    if (detail.code === 'assistant_unavailable') {
      setLastFailed(null);
      pushAssistant(ui.unavailable, true);
      return true;
    }
    return false;
  };

  /** A streamed delta: open the bubble on the first, append to it after. */
  const appendDelta = (text: string) => {
    const id = streamIdRef.current;
    if (id) {
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, content: m.content + text } : m)));
      return;
    }
    const fresh = newId();
    streamIdRef.current = fresh;
    setStreaming(true);
    setMessages((prev) => [...prev, { id: fresh, role: 'assistant', content: text }]);
  };

  /** A turn that failed after its stream began: the half-written bubble goes. */
  const discardStream = () => {
    const id = streamIdRef.current;
    streamIdRef.current = null;
    if (id) setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  /**
   * The pass a send carries (ADR-123). `log` attaches one when it has one and
   * never waits; `enforce` waits for it. After a refusal the pass is always a
   * fresh one. Rejects with a HumanCheckError when no pass can be had.
   */
  const passForSend = async (afterRefusal: boolean): Promise<string | null> => {
    if (afterRefusal) return humanCheck.obtain({ fresh: true });
    const ready = humanCheck.current();
    if (ready) return ready;
    const mode = humanCheck.mode();
    if (mode === 'enforce') return humanCheck.obtain();
    if (mode === 'log') humanCheck.obtain().catch(() => undefined);
    return null;
  };

  /** A 403 asking for the check: adopt its configuration and drop the stale pass. */
  const refusedByHumanCheck = (status: number, body: Record<string, unknown> | null): boolean => {
    if (!isHumanCheckRefusal(status, body)) return false;
    humanCheck.learn(body?.humanCheck);
    humanCheck.invalidate();
    return true;
  };

  /**
   * One attempt on the wire. `refused` = the human check turned it away
   * before anything was admitted, metered or stored; anything else that goes
   * wrong throws.
   */
  const sendOnce = async (
    turn: OutgoingTurn,
    priorMessages: Message[],
    humanPass: string | null,
  ): Promise<'settled' | 'refused'> => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), MESSAGE_TIMEOUT_MS);
    const payload = {
      message: turn.message,
      tenantId,
      // What the reply must be written in, and what the backend compares
      // against the tenant's corpus language to decide whether the question
      // needs translating before it is searched.
      language,
      ...(sessionIdRef.current ? { sessionId: sessionIdRef.current } : {}),
      history: priorMessages
        .slice(0, -1)
        .filter((m) => !m.error && m.role !== 'admin')
        .map((m) => ({ role: m.role, content: m.content })),
      // A booking details form's values (ADR-110); nothing else changes.
      ...(turn.details ? { details: turn.details } : {}),
      ...(humanPass ? { humanPass } : {}),
    };
    try {
      // The streaming transport first, when the server advertised one
      // (ADR-103 §4). The one fallback is a URL that could not be reached
      // at all — nothing after the first byte is resent, because a turn
      // that reached the backend was admitted and metered.
      if (streamUrl) {
        const outcome = await streamMessage(
          `${streamUrl}/chat/message`,
          payload,
          { onSession: adoptSession, onDelta: appendDelta },
          ctrl.signal,
        );
        if (outcome.kind === 'done') {
          applyReply(outcome.body);
          return 'settled';
        }
        if (outcome.kind === 'error') {
          discardStream();
          if (refusedByHumanCheck(outcome.status, outcome.body)) return 'refused';
          if (applyBlocked(outcome.status, outcome.body)) return 'settled';
          throw new Error(`HTTP ${outcome.status}`);
        }
        if (outcome.kind === 'broken') {
          discardStream();
          throw new Error('stream ended early');
        }
        console.warn(
          `[PweChat] The streaming endpoint ${streamUrl} could not be reached (${outcome.reason}); sending over ${apiUrl} instead.`,
        );
      }

      const res = await fetch(`${apiUrl}/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: ctrl.signal,
        body: JSON.stringify(payload),
      });

      if (res.status === 403) {
        const detail = await res.json().catch(() => ({}));
        if (refusedByHumanCheck(403, detail)) return 'refused';
        if (applyBlocked(403, detail)) return 'settled';
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      applyReply(await res.json());
      return 'settled';
    } finally {
      clearTimeout(timer);
    }
  };

  /** priorMessages must already end with the visitor's bubble for `turn`. */
  const doSend = async (turn: OutgoingTurn, priorMessages: Message[]) => {
    setLoading(true);
    try {
      // A human-check refusal spent nothing (ADR-123), so the turn goes again,
      // once, with a fresh pass. A second refusal is a failed check.
      let outcome = await sendOnce(turn, priorMessages, await passForSend(false));
      if (outcome === 'refused') outcome = await sendOnce(turn, priorMessages, await passForSend(true));
      if (outcome === 'refused') throw new HumanCheckError('refused_again');
    } catch (err) {
      discardStream();
      setLastFailed(turn);
      pushAssistant(err instanceof HumanCheckError ? ui.humanCheckFailed : errorMessage, true);
    } finally {
      setLoading(false);
      setStreaming(false);
      // Polling goes hot from HERE, once the reply and any new sessionId are
      // known: bumping before the request restarted the poll effect (and a
      // history GET) once per send and again on the adopted session.
      bumpActivity();
    }
  };

  /**
   * Every send: typed text, a suggestion chip, a booking step. `shown` is the
   * visitor's bubble; `turn` is what goes on the wire — the same text for
   * anything typed, and for a booking step the value the server reads under
   * the label the visitor tapped. Returns false when nothing was sent.
   */
  const sendTurn = (shown: string, turn: OutgoingTurn): boolean => {
    if (!shown || !turn.message || loading) return false;
    const updated = [...messages, { id: newId(), role: 'user' as const, content: shown }];
    setMessages(updated);
    setInput('');
    setSuggestions([]);
    // The last turn's booking steps go with the send, not with the answer.
    setActions([]);
    setLastFailed(null);
    // Keep the caret where it was: the input stays enabled (readOnly while
    // loading) so the browser never blurs it, and keyboard users are not sent
    // back to the box after every reply (review S7). A chip tap on a phone
    // does not pull the keyboard up, though.
    const refocus = !isMobile || document.activeElement === inputRef.current;
    doSend(turn, updated).then(() => {
      if (refocus) inputRef.current?.focus();
    });
    return true;
  };

  const sendText = (text: string) => {
    sendTurn(text, { message: text });
  };

  const retry = () => {
    if (!lastFailed || loading) return;
    const turn = lastFailed;
    setLastFailed(null);
    setActions([]);
    // Strip the failure bubble(s); the user message stays and the turn is
    // re-sent as-is — a slot's instant, a details form's values included.
    const base = messages.filter((m) => !m.error);
    setMessages(base);
    doSend(turn, base);
  };

  const sendMessage = (e: FormEvent) => {
    e.preventDefault();
    sendText(input.trim());
  };

  const startNewChat = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = setTimeout(() => setConfirmClear(false), 3000);
      return;
    }
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    setConfirmClear(false);
    setMessages([{ id: 'greeting', role: 'assistant', content: greeting }]);
    setSuggestions(config.suggestions);
    setSessionId(null);
    setLastFailed(null);
    setReveal(null);
    setPollStopped(false);
    setLeadOpen(false);
    setLeadSubmitted(false);
    setLeadSuggested(false);
    setLeadChipHidden(false);
    setLinkChipHidden(false);
    setActions([]);
    seenAdminIdsRef.current = new Set();
    lastSkRef.current = null;
    clearChat(tenantId);
    bumpActivity();
  };

  // ── Operator replies ─────────────────────────────────────────
  useHistoryPolling({
    enabled: open && !pollStopped,
    apiUrl,
    tenantId,
    sessionId,
    activityAt,
    cursorRef: lastSkRef,
    seenAdminIdsRef,
    onAdminMessages: (contents) => {
      setMessages((prev) => [...prev, ...contents.map((content) => ({ id: newId(), role: 'admin' as const, content }))]);
      setAnnouncement(stripMarkdown(contents[contents.length - 1]));
    },
    onGone: () => setPollStopped(true),
    onClosed: () => setPollStopped(true),
  });

  // ── Lead capture ─────────────────────────────────────────────
  const handleLead = async (lead: LeadInput): Promise<string | null> => {
    const sid = sessionIdRef.current;
    const request = {
      tenantId,
      ...(sid ? { sessionId: sid } : {}),
      ...lead,
      conversation_summary: summariseConversation(messagesRef.current),
    };
    // The same pass a turn carries (ADR-123), and the same one retry.
    let outcome: LeadOutcome;
    try {
      const humanPass = await passForSend(false);
      outcome = await submitLead(apiUrl, { ...request, ...(humanPass ? { humanPass } : {}) });
      if (outcome.kind === 'human_check') {
        humanCheck.learn(outcome.humanCheck);
        humanCheck.invalidate();
        const fresh = await passForSend(true);
        outcome = await submitLead(apiUrl, { ...request, ...(fresh ? { humanPass: fresh } : {}) });
      }
    } catch {
      return ui.humanCheckFailed;
    }
    switch (outcome.kind) {
      case 'ok':
        setLeadOpen(false);
        setLeadSubmitted(true);
        pushAssistant(fill(ui.leadThanks, { name: lead.name }));
        return null;
      case 'disabled':
        // Switched off in the console after this page loaded: no chip from
        // now on, and the visitor is told their details did not go through.
        setLeadOpen(false);
        setLeadDisabled(true);
        pushAssistant(ui.leadError, true);
        return null;
      case 'blocked':
        setLeadOpen(false);
        setLeadDisabled(true);
        pushAssistant(outcome.code === 'origin_not_allowed' ? originBlockedMessage : ui.unavailable, true);
        return null;
      case 'invalid':
        return ui.leadError;
      case 'human_check':
        return ui.humanCheckFailed;
      case 'retry':
        return outcome.code === 'lead_cap' ? ui.leadCap : ui.leadError;
    }
  };

  const openLeadForm = () => {
    setLeadOpen(true);
    setLastFailed(null);
    bumpActivity();
  };

  // ── Booking steps (ADR-110) ──────────────────────────────────
  /** A details submit: a line that never carries the email, and the values as `details`. */
  const submitDetails = (details: BookingDetails): boolean => {
    const line = bookingDetailsLine(details, ui);
    return sendTurn(line, { message: line, details });
  };

  /** Cancel closes the form and leaves the conversation as it is; typing still works. */
  const cancelDetails = () => {
    setActions((prev) => prev.filter((a) => a.type !== 'collect_details'));
    if (!isMobile) inputRef.current?.focus();
  };

  // ── Feedback about the assistant (feedback.ts) ───────────────
  const openFeedback = () => {
    // The two-tap "new chat" is not on screen while the form is.
    setConfirmClear(false);
    setFeedbackOpen(true);
  };

  const closeFeedback = () => {
    setFeedbackOpen(false);
    bumpActivity();
  };

  /** Resolves to the error to show on the form, or null once it has gone. */
  const handleFeedback = async (input: FeedbackInput): Promise<string | null> => {
    if (!feedbackApiUrl) return ui.feedbackError;
    // The honeypot was filled in: thank the bot and send nothing. The lead
    // route makes this call server-side; the feedback service has no honeypot
    // of its own, so this is the only place it can be made.
    if (input.website) return null;
    const outcome = await submitFeedback(
      feedbackApiUrl,
      feedbackRequest(input, { tenantId, language, page: feedbackPage(window.location) }),
    );
    if (outcome.kind === 'ok') {
      setAnnouncement(ui.feedbackThanks);
      return null;
    }
    console.warn(`[PweChat] Feedback could not be sent to ${feedbackApiUrl}/v1/feedback (${outcome.code}).`);
    return ui.feedbackError;
  };

  // ── Placement (corner + offset, with mobile clamp) ───────────
  // The VISUAL viewport, so the mobile sheet fits above the soft keyboard
  // (review S9); `innerHeight` does not move for the keyboard on iOS.
  const [viewport, setViewport] = useState<{ w: number; h: number; top: number }>({ w: 1024, h: 768, top: 0 });
  useEffect(() => {
    const vv = window.visualViewport;
    const update = () =>
      setViewport({
        w: Math.round(vv ? vv.width : window.innerWidth),
        h: Math.round(vv ? vv.height : window.innerHeight),
        top: Math.round(vv ? vv.offsetTop : 0),
      });
    update();
    window.addEventListener('resize', update);
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
    };
  }, []);

  const isLeft = position === 'bottom-left';
  const isExpanded = panelSize === 'expanded';
  // Height counts too: a landscape phone is 380px tall and the floating
  // 520px card cannot fit; it gets the full-screen sheet like a portrait one.
  const isMobile = viewport.w <= 480 || viewport.h <= 480;

  // On mobile the panel is clamped to the viewport; clamp offsets to the same
  // box so nothing anchors off-screen.
  const panelW = Math.min(isExpanded ? 560 : 380, viewport.w - 48);
  const panelH = Math.min(isExpanded ? viewport.h - 48 : 520, viewport.h - 48);
  const maxSide = Math.max(0, viewport.w - 24 - panelW);
  const maxBottom = Math.max(0, viewport.h - 24 - panelH);
  const sideOffset = isMobile ? Math.min(offset.side, maxSide) : offset.side;
  const bottomOffset = isMobile ? Math.min(offset.bottom, maxBottom) : offset.bottom;

  // The full-screen sheet covers the page; stop the page scrolling under it.
  useEffect(() => {
    if (!open || !isMobile) return;
    const body = document.body;
    const previous = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previous;
    };
  }, [open, isMobile]);

  const styles = useMemo(
    () =>
      buildStyles({
        brandColor,
        isLeft,
        isMobile,
        isExpanded,
        viewport,
        sideOffset,
        bottomOffset,
      }),
    [brandColor, isLeft, isMobile, isExpanded, viewport, sideOffset, bottomOffset],
  );

  // What the latest turn's booking steps draw, labelled in the page language.
  const booking = useMemo(() => {
    const booked = actions.find((a): a is Extract<ChatAction, { type: 'booked' }> => a.type === 'booked');
    return {
      choices: bookingChoices(actions, language, ui),
      details: actions.find((a): a is Extract<ChatAction, { type: 'collect_details' }> => a.type === 'collect_details'),
      booked,
      bookedAt: booked ? formatSlotLabel(booked.start, language, booked.timezone) : '',
      hasControls: actions.some(isBookingControl),
    };
  }, [actions, language, ui]);

  // ── Keyframes (injected once per page) ───────────────────────
  useEffect(() => {
    if (document.getElementById(KEYFRAMES_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = KEYFRAMES_STYLE_ID;
    style.textContent = KEYFRAMES_CSS;
    document.head.appendChild(style);
  }, []);

  // ── Render ───────────────────────────────────────────────────
  const visibleContent = (msg: Message): string =>
    reveal && reveal.id === msg.id ? msg.content.slice(0, reveal.chars) : msg.content;

  const dialogLabel = fill(ui.chatDialogLabel, { name: businessName });
  const teamLabel = fill(ui.teamLabel, { name: businessName });

  const launcherIcon = avatarUrl ? (
    // The button carries the accessible name; the picture is decorative.
    <img src={avatarUrl} alt="" width="36" height="36" style={{ borderRadius: '50%', objectFit: 'cover' }} />
  ) : (
    <ChatIcon />
  );

  const chipHover = {
    onMouseEnter: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.background = styles.chipHoverBackground;
      e.currentTarget.style.borderColor = styles.accent;
    },
    onMouseLeave: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.background = 'rgba(255,255,255,0.8)';
      e.currentTarget.style.border = styles.chipBorder;
    },
  };

  const showLeadChip = leadCaptureEnabled && !leadDisabled && !leadSubmitted && !leadChipHidden;
  const chips: Chip[] = suggestions.map((text) => ({ kind: 'text', text }));
  // The business's own call to action leads the row: the one chip a client
  // put there on purpose, and the one that is always there.
  if (linkChip && !linkChipHidden) chips.unshift({ kind: 'link', label: linkChip.label, url: linkChip.url });
  // The lead chip sits last — until a reply asks for it, when it comes first.
  if (showLeadChip) {
    if (leadSuggested) chips.unshift({ kind: 'lead' });
    else chips.push({ kind: 'lead' });
  }
  // A booking control (slot or option chips, the details form, confirm) stands
  // in for the suggestions while it is shown; the text input stays usable.
  const showBookingControls = bookingSettled && !leadOpen && booking.hasControls;
  const showChips = chips.length > 0 && !loading && !reveal && !lastFailed && !leadOpen && !showBookingControls && !feedbackOpen;

  // The human check's frame is ALWAYS the first child, open or closed: a frame
  // that moves to another parent is remounted and starts the challenge again.
  if (!open) {
    if (hostLaunched) return <>{humanCheckFrame}</>;
    return (
      <>
        {humanCheckFrame}
        <div style={styles.bubbleGlow} />
        {/* The invitation, beside the bubble, in the visitor's words. Desktop
            only: a phone has no room beside the bubble and the bubble is the
            whole affordance there. Hidden from assistive tech and the tab
            order on purpose — the bubble already carries the accessible name
            ("Open chat"), and a second focus stop that does the same thing is
            noise to a screen reader; a pointer gets the larger target. */}
        {launcherLabel && !isMobile && (
          <div
            style={styles.launcherLabel}
            onClick={() => setOpen(true)}
            aria-hidden="true"
            data-pwe="launcher-label"
          >
            {launcherLabel}
          </div>
        )}
        <button
          ref={launcherRef}
          type="button"
          onClick={() => setOpen(true)}
          style={styles.bubble}
          aria-label={ui.openChat}
          title={ui.chatWithUs}
          data-pwe="launcher"
        >
          {launcherIcon}
        </button>
      </>
    );
  }

  return (
    <>
      {humanCheckFrame}
      {/* Floating button (shows X when open; hidden under the mobile sheet) */}
      {!hostLaunched && !isMobile && (
        <>
          <div style={styles.bubbleGlow} />
          <button
            ref={launcherRef}
            type="button"
            onClick={() => closePanel(true)}
            style={styles.bubble}
            aria-label={ui.closeChat}
            data-pwe="launcher"
          >
            <CloseIcon size={20} />
          </button>
        </>
      )}

      {/* Chat panel */}
      <div style={styles.panel} role="dialog" aria-label={dialogLabel} onKeyDown={onPanelKeyDown} data-pwe="panel">
        {/* Screen-reader announcements: complete replies only, so the
            typewriter reveal doesn't spam partial text. */}
        <div style={srOnlyStyle} aria-live="polite">
          {announcement}
        </div>

        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            {avatarUrl && (
              <img src={avatarUrl} alt="" width="32" height="32" style={{ flexShrink: 0, borderRadius: '50%', objectFit: 'cover' }} />
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '15px', color: '#1a1a2e', letterSpacing: '-0.01em' }} data-pwe="title">
                {businessName}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '1px' }}>{feedbackOpen ? ui.feedbackSubtitle : subtitle}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {confirmClear && (
              // Inline, not only in a tooltip: the second tap is otherwise a
              // guess for anyone not hovering a mouse (review N7).
              <span role="status" style={{ fontSize: '11px', color: '#b91c1c', whiteSpace: 'nowrap' }}>
                {ui.tapAgainToClearShort}
              </span>
            )}
            {feedbackApiUrl && (
              // A toggle: the same button takes the visitor back to the
              // conversation the form is standing in for.
              <button
                ref={feedbackButtonRef}
                type="button"
                onClick={feedbackOpen ? closeFeedback : openFeedback}
                style={{
                  ...styles.iconButton,
                  ...(feedbackOpen ? { background: styles.chipHoverBackground, color: styles.accent } : {}),
                }}
                aria-label={feedbackOpen ? ui.backToChat : ui.sendFeedback}
                title={feedbackOpen ? ui.backToChat : ui.sendFeedback}
                data-pwe="feedback"
              >
                {feedbackOpen ? <ChatIcon size={14} strokeWidth={2} /> : <FeedbackIcon />}
              </button>
            )}
            {!feedbackOpen && messages.length > 1 && (
              <button
                type="button"
                onClick={startNewChat}
                style={{
                  ...styles.iconButton,
                  ...(confirmClear ? { background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c' } : {}),
                }}
                aria-label={confirmClear ? ui.confirmNewChat : ui.newChat}
                title={confirmClear ? ui.tapAgainToClear : ui.newChat}
                data-pwe="new-chat"
              >
                {confirmClear ? <CheckIcon /> : <PlusIcon />}
              </button>
            )}
            {!isMobile && (
              <button
                type="button"
                onClick={() => setPanelSize(isExpanded ? 'default' : 'expanded')}
                style={styles.iconButton}
                aria-label={isExpanded ? ui.collapseChat : ui.expandChat}
                title={isExpanded ? ui.collapseChat : ui.expandChat}
              >
                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </button>
            )}
            <button type="button" onClick={() => closePanel(true)} style={styles.iconButton} aria-label={ui.closeChat} data-pwe="close">
              <CloseIcon />
            </button>
          </div>
        </div>

        {feedbackOpen && (
          <FeedbackForm ui={ui} styles={styles} brandColor={brandColor} onSubmit={handleFeedback} onClose={closeFeedback} />
        )}

        {/* Messages — hidden, not unmounted, behind the feedback form: a
            half-filled lead or booking form in the transcript survives it. */}
        <div style={feedbackOpen ? { display: 'none' } : styles.messages} data-pwe="messages">
          {messages.map((msg) => (
            <div
              key={msg.id}
              style={msg.role === 'user' ? styles.userBubble : msg.role === 'admin' ? styles.adminBubble : styles.assistantBubble}
              data-pwe="message"
              data-role={msg.role}
              data-error={msg.error ? 'true' : undefined}
            >
              {msg.role === 'admin' && (
                <div style={{ fontSize: '11px', fontWeight: 600, color: styles.accent, marginBottom: '2px' }}>{teamLabel}</div>
              )}
              {msg.role === 'user' ? (
                msg.content
              ) : (
                <span dangerouslySetInnerHTML={{ __html: renderMarkdown(visibleContent(msg), styles.accent) }} />
              )}
            </div>
          ))}
          {loading && !streaming && (
            <div style={styles.assistantBubble} role="status" aria-label={ui.typing} data-pwe="typing">
              <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }} aria-hidden="true">
                {[0, 1, 2].map((dot) => (
                  <span
                    key={dot}
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: '#8b8fa3',
                      animation: `pweb-chat-pulse 1.2s ease-in-out ${dot * 0.2}s infinite`,
                    }}
                  />
                ))}
              </span>
            </div>
          )}
          {/* A booking that went through: it informs and adds no button. Plain text, never markdown. */}
          {bookingSettled && booking.booked && (
            <div style={styles.assistantBubble} data-pwe="booking-booked">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: styles.accent }}>
                <CheckIcon />
                {ui.bookingBooked}
              </div>
              <div style={{ fontWeight: 600, marginTop: '2px' }}>{booking.bookedAt}</div>
              {booking.booked.service && <div>{booking.booked.service}</div>}
              {booking.booked.bookingId && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  {fill(ui.bookingReference, { id: booking.booked.bookingId })}
                </div>
              )}
            </div>
          )}
          {/* ONE details form (ADR-110 Am.6): the booking's and the lead's are the same component. */}
          {showBookingControls && booking.details && (
            <DetailsForm
              purpose="booking"
              ui={ui}
              styles={styles}
              brandColor={brandColor}
              fields={booking.details.fields}
              required={booking.details.required}
              onSubmit={submitDetails}
              onCancel={cancelDetails}
            />
          )}
          {leadOpen && (
            <DetailsForm
              purpose="lead"
              ui={ui}
              styles={styles}
              brandColor={brandColor}
              form={leadForm}
              onSubmit={handleLead}
              onCancel={() => setLeadOpen(false)}
            />
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Retry after a failed send */}
        {lastFailed && !loading && !feedbackOpen && (
          <div style={{ padding: '6px 16px 2px', display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button type="button" onClick={retry} style={styles.chip} {...chipHover} data-pwe="retry">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <RetryIcon />
                {ui.tryAgain}
              </span>
            </button>
          </div>
        )}

        {/* The latest turn's booking choices (ADR-110), in place of the suggestions.
            Each sends its value verbatim under the label the visitor tapped. */}
        {showBookingControls && !feedbackOpen && booking.choices.length > 0 && (
          <div style={{ padding: '6px 16px 2px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }} data-pwe="booking-actions">
            {booking.choices.map((choice, i) => (
              <button
                key={`${i}-${choice.kind}-${choice.message}`}
                type="button"
                onClick={() => sendTurn(choice.label, { message: choice.message })}
                style={choice.kind === 'yes' ? { ...styles.chip, background: brandColor, borderColor: brandColor, color: styles.ink } : styles.chip}
                {...(choice.kind === 'yes' ? {} : chipHover)}
                data-pwe={CHOICE_PWE[choice.kind]}
              >
                {choice.label}
              </button>
            ))}
          </div>
        )}

        {/* Suggested replies (held back until the reveal finishes) */}
        {showChips && (
          <div style={{ padding: '6px 16px 2px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }} data-pwe="chips">
            <button
              type="button"
              onClick={() => {
                setSuggestions([]);
                setLeadChipHidden(true);
                setLinkChipHidden(true);
              }}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(0,0,0,0.06)',
                color: '#6b7280',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                padding: 0,
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0,0,0,0.06)';
              }}
              aria-label={ui.dismissSuggestions}
              title={ui.dismissSuggestions}
            >
              <CloseIcon size={10} strokeWidth={3} />
            </button>
            {chips.map((chip, i) =>
              chip.kind === 'link' ? (
                // A link, not a message: it goes somewhere. The business's own
                // site opens in place; anywhere else in a new tab.
                <a
                  key="link"
                  href={chip.url}
                  {...(isSameOrigin(chip.url) ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                  style={{ ...styles.chip, display: 'inline-block', textDecoration: 'none' }}
                  {...chipHover}
                  data-pwe="link-chip"
                >
                  {chip.label}
                </a>
              ) : chip.kind === 'lead' ? (
                <button key="lead" type="button" onClick={openLeadForm} style={styles.chip} {...chipHover} data-pwe="lead-chip">
                  {ui.leaveDetails}
                </button>
              ) : (
                <button key={`${i}-${chip.text}`} type="button" onClick={() => sendText(chip.text)} style={styles.chip} {...chipHover} data-pwe="chip">
                  {chip.text}
                </button>
              ),
            )}
          </div>
        )}

        {/* Privacy line: shown whenever a policy URL is known */}
        {privacyUrl && !feedbackOpen && (
          <div style={styles.privacy} data-pwe="privacy">
            {privacyNotice}{' '}
            <a href={privacyUrl} target="_blank" rel="noopener noreferrer" style={{ color: styles.accent, textDecoration: 'underline' }}>
              {privacyLinkLabel}
            </a>
            .
          </div>
        )}

        {/* Input bar — hidden, not unmounted, behind the feedback form, so a
            half-typed message survives it. */}
        <form onSubmit={sendMessage} style={feedbackOpen ? { display: 'none' } : styles.inputBar}>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={ui.typeAMessage}
            aria-label={ui.typeAMessage}
            aria-busy={loading}
            maxLength={2000}
            readOnly={loading}
            style={styles.input}
            data-pwe="input"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            style={sendButtonStyle(brandColor, styles.ink, !loading && input.trim() !== '')}
            aria-label={ui.sendMessage}
            data-pwe="send"
          >
            <SendIcon />
          </button>
        </form>
      </div>
    </>
  );
}
